import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Clock3, FileWarning, FolderSearch, Search, ShieldCheck, Star, X } from 'lucide-react';
import PageIntro, { HelpText, QuickActions } from '../components/ui/PageIntro';
import Badge from '../components/ui/Badge';
import { searchApi, getApiErrorMessage } from '../api';
import { formatStatus, getStatusVariant } from '../utils/formatters';

const RECENT_KEY = 'workspace_recent_searches';
const TYPE_META = {
  all: { label: uiText("Все результаты") },
  audit: { label: uiText("Аудиты"), icon: ShieldCheck },
  issue: { label: uiText("Замечания"), icon: FileWarning },
  company: { label: uiText("Компании"), icon: Building2 },
};

function readRecentSearches() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter(Boolean).slice(0, 8) : [];
  } catch {
    return [];
  }
}

function storeRecentSearch(query) {
  const value = (query || '').trim();
  if (!value) return readRecentSearches();
  const next = [value, ...readRecentSearches().filter((item) => item !== value)].slice(0, 8);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  return next;
}

function ResultCard({ item, onOpen }) {
  const Icon = TYPE_META[item.entity_type]?.icon || FolderSearch;
  const hasKnownStatus = ['audit', 'issue'].includes(item.entity_type) && item.subtitle;
  return (
    <button type="button" className="workspace-search-result" onClick={() => onOpen(item)}>
      <span className="workspace-search-result-icon"><Icon size={17} /></span>
      <div className="workspace-search-result-copy">
        <div className="workspace-search-result-head">
          <div className="workspace-item-title">{item.title}</div>
          <Badge variant="neutral">{TYPE_META[item.entity_type]?.label || item.entity_type}</Badge>
        </div>
        <div className="workspace-item-meta">
          {hasKnownStatus ? <Badge variant={getStatusVariant(item.subtitle)}>{formatStatus(item.subtitle, item.entity_type)}</Badge> : (item.subtitle || uiText("Дополнительные сведения не указаны"))}
        </div>
      </div>
      <ChevronRight size={17} className="workspace-search-result-arrow" />
    </button>
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [viewName, setViewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [recentSearches, setRecentSearches] = useState(() => readRecentSearches());

  const loadSavedViews = async () => {
    try {
      const res = await searchApi.savedViews();
      setSavedViews((res.data || []).filter(view => !String(view.entity_type).startsWith('registry_') && view.entity_type !== 'report_preferences'));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadSavedViews();
  }, []);

  const run = async (forcedQuery) => {
    const actual = (forcedQuery ?? q).trim();
    if (!actual) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await searchApi.query(actual);
      setQ(actual);
      setItems(res.data?.items || []);
      setActiveType('all');
      setRecentSearches(storeRecentSearch(actual));
    } catch (e) {
      setMessage(getApiErrorMessage(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentView = async () => {
    const name = viewName.trim();
    const query = q.trim();
    if (!name || !query) {
      setMessage(uiText("Укажите название представления и выполните поисковый запрос."));
      return;
    }
    try {
      await searchApi.saveView({ name, entity_type: 'search', filters: { q: query } });
      setViewName('');
      setMessage(uiText("Представление сохранено."));
      await loadSavedViews();
    } catch (e) {
      setMessage(getApiErrorMessage(e));
    }
  };

  const go = (item) => {
    if (item.entity_type === 'audit') navigate(`/audits/${item.entity_id}`);
    else if (item.entity_type === 'issue') navigate('/issues');
    else if (item.entity_type === 'company') navigate('/companies');
  };

  const counts = useMemo(() => {
    return items.reduce((acc, item) => {
      const key = item.entity_type || 'other';
      acc[key] = (acc[key] || 0) + 1;
      acc.all = (acc.all || 0) + 1;
      return acc;
    }, { all: 0 });
  }, [items]);

  const filteredItems = useMemo(() => {
    if (activeType === 'all') return items;
    return items.filter((item) => item.entity_type === activeType);
  }, [activeType, items]);

  const grouped = useMemo(() => {
    return filteredItems.reduce((acc, item) => {
      const type = item.entity_type || 'other';
      acc[type] = acc[type] || [];
      acc[type].push(item);
      return acc;
    }, {});
  }, [filteredItems]);

  return (
    <div className="workspace-page">
      <PageIntro
        eyebrow={uiText("Единая навигация")}
        title={uiText("Глобальный поиск")}
        description={uiText("Ищите аудиты, замечания и компании из одного окна. Результаты можно быстро сохранять как персональные представления.")}
      >
        <HelpText>{uiText("Сначала введите свободный запрос, затем при необходимости сузьте список по типу сущности. Для повторяющихся сценариев сохраните представление.")}</HelpText>
        <QuickActions>
          <Link className="btn btn-outline" to="/notifications">{uiText("Уведомления")}</Link>
          <Link className="btn btn-outline" to="/dashboard">{uiText("Обзор")}</Link>
        </QuickActions>
      </PageIntro>

      <div className="card workspace-search-command-card">
        <div className="card-body">
          <form className="workspace-search-command" onSubmit={(event) => { event.preventDefault(); run(); }}>
            <label className="workspace-search-label" htmlFor="global-search-query">{uiText("Что найти")}</label>
            <div className="workspace-search-input-wrap">
              <Search size={16} className="workspace-search-input-icon" />
              <input
                id="global-search-query"
                className="form-control workspace-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={uiText("Например: магазин Казань, плановый аудит, замечание по санзоне")}
              />
              {q ? (
                <button type="button" className="workspace-search-clear" onClick={() => setQ('')} aria-label={uiText("Очистить")}>
                  <X size={14} />
                </button>
              ) : null}
            </div>
            <button type="submit" className="btn btn-primary workspace-search-submit" disabled={loading || !q.trim()}><Search size={15} /> {loading ? uiText("Ищем…") : uiText("Найти")}</button>
          </form>

          {message ? <div className={`alert ${message.includes(uiText("сохранено")) ? 'alert-success' : 'alert-error'}`}>{message}</div> : null}

          <div className="workspace-search-filterbar">
            <div className="workspace-search-summary" aria-live="polite">
              {q ? <><strong>{counts.all || 0}</strong>{uiText(" результатов по запросу «")}{q}»</> : uiText("Введите название, номер или ключевое слово")}
            </div>
            <div className="workspace-chip-row" aria-label={uiText("Тип результата")}>
              {Object.keys(TYPE_META).map((key) => (
                <button type="button"
                  key={key}
                  className={`workspace-segment workspace-segment-compact ${activeType === key ? 'is-active' : ''}`}
                  onClick={() => setActiveType(key)}
                >
                  {TYPE_META[key].label}
                  <span className="workspace-segment-count">{counts[key] || 0}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="workspace-search-layout">
        <div className="card workspace-search-results-card">
          <div className="card-header workspace-search-results-header">
            <div><div className="card-title workspace-search-results-title"><FolderSearch size={17} /> <span>{uiText("Результаты поиска")}</span></div><div className="workspace-muted">{uiText("Откройте строку, чтобы перейти к объекту")}</div></div>
            {filteredItems.length ? <Badge variant="neutral">{uiText("Показано: ")}{filteredItems.length}</Badge> : null}
          </div>
          <div className="card-body workspace-search-results-body">
            {!q ? <div className="workspace-empty-state"><FolderSearch size={22} /><div className="workspace-empty-state-title">{uiText("Начните поиск")}</div><div className="workspace-empty-state-text">{uiText("Введите название аудита, компании или ключевое слово из замечания.")}</div></div> : null}
            {q && !loading && filteredItems.length === 0 ? <div className="workspace-empty-state"><Search size={22} /><div className="workspace-empty-state-title">{uiText("Ничего не найдено")}</div><div className="workspace-empty-state-text">{uiText("Измените формулировку запроса или выберите «Все результаты».")}</div></div> : null}
            {Object.entries(grouped).map(([type, rows]) => (
              <div key={type} className="workspace-search-result-group">
                <div className="workspace-search-result-group-title"><span>{TYPE_META[type]?.label || type}</span><span>{rows.length}</span></div>
                <div className="workspace-search-result-list">
                  {rows.map((item) => <ResultCard key={`${item.entity_type}-${item.entity_id}`} item={item} onOpen={go} />)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="card workspace-search-shortcuts">
          <div className="card-header"><div><div className="card-title">{uiText("Быстрый доступ")}</div><div className="workspace-muted">{uiText("Повторите недавний запрос или сохраните текущий")}</div></div></div>
          <div className="card-body workspace-search-shortcuts-body">
            <div className="workspace-search-save">
              <label className="workspace-list-title" htmlFor="saved-search-name"><Star size={15} />{uiText(" Сохранить текущий запрос")}</label>
              <input id="saved-search-name" className="form-control" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder={uiText("Название представления")} />
              <button type="button" className="btn btn-outline" onClick={saveCurrentView} disabled={!q.trim() || !viewName.trim()}><Star size={15} />{uiText(" Сохранить")}</button>
            </div>
            <div className="workspace-search-shortcut-section">
              <div className="workspace-list-title"><Clock3 size={15} />{uiText(" Недавние запросы")}</div>
              <div className="workspace-chip-row workspace-search-recent-list">
                {recentSearches.length === 0 ? <div className="workspace-empty">{uiText("Недавних запросов пока нет.")}</div> : recentSearches.map((item) => (
                  <button type="button" key={item} className="workspace-chip" onClick={() => run(item)}>{item}</button>
                ))}
              </div>
            </div>

            <div className="workspace-search-shortcut-section">
              <div className="workspace-list-title"><Star size={15} />{uiText(" Сохранённые представления")}</div>
              <div className="workspace-list workspace-search-saved-list">
                {savedViews.length === 0 ? <div className="workspace-empty">{uiText("Пока ничего не сохранено.")}</div> : savedViews.map((view) => (
                  <button type="button" key={view.id} className="workspace-list-item" onClick={() => run(view.filters?.q || '')}>
                    <div>
                      <div className="workspace-item-title">{view.name}</div>
                      <div className="workspace-item-meta">{view.filters?.q || uiText("Без поискового запроса")}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
